import {NoteGroupIcon} from "crqlar-react-common/dist/components/note/NoteGroupIcon";
import {useNoteGroupIndex} from "crqlar-react-common/dist/utils/hooks/notes/useNoteGroupIndex";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {Stack, Switch} from "@mui/material";
import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import Property from "crqlar-react-common/dist/components/Property";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {useQuery} from "react-query";
import {MyAccountService} from "crqlar-react-common/dist/services/identity/myAccount.service";

interface Props {
    data: UseEntityData<Profile>
}

const InternalNotes = (props: Props) => {

    const {t} = useTranslation()
    const profile = props.data.item
    const setProfile = props.data.setItem
    const updateField = (field: keyof Profile, value: any) => {
        setProfile({...profile, [field]: value})
    }

    const noteGroupIndex = useNoteGroupIndex()

    // Fetch current user to set as userEmailSource when adding a new note
    const currentUser = useQuery(['myAccount'], async () => {
        return await MyAccountService.getMyAccount()
    }).data

    return (
        <Stack gap={{xs: 3, sm: 2.5}}>
            {
                profile.internalNotes?.map((internalNote, index) => {
                    const provider = internalNote.externalInfo?.[0]?.provider
                    const disabled = !!provider
                    return (
                        <Property
                            orientation={'vertical'}
                            key={index}
                            title={t('Note') + ' ' + (index + 1)}
                            subtitle={provider}
                            sx={{
                                title: {
                                    mt: 2,
                                    fontWeight: 'bold'
                                }
                            }}
                        >
                            <Stack gap={1} width={'100%'}>
                                <Property title={t('Note content')} labelAlignment={'top'}>
                                    <TextField
                                        id={`internal-note-${index}`}
                                        size={'small'}
                                        value={internalNote.note || ''}
                                        onChange={(e) => {
                                            const internalNotes = [...(profile.internalNotes || [])]
                                            internalNotes[index] = {
                                                ...internalNotes[index],
                                                note: e.target.value,
                                                // Every time we update the note, we also update the updateDate
                                                updateDate: new Date()
                                            }
                                            updateField('internalNotes', internalNotes)
                                        }}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        disabled={disabled}
                                        InputProps={{
                                            startAdornment: (
                                                <NoteGroupIcon
                                                    note={internalNote}
                                                    index={noteGroupIndex}
                                                    sx={{mr: 1, alignSelf: 'flex-start', mt: '3px'}}
                                                />
                                            ),
                                        }}
                                    />
                                </Property>

                                <Property title={t('Public')} labelAlignment={'top'}>
                                    <Switch
                                        id={'internal-note-public-switch'}
                                        checked={internalNote.guestViewable}
                                        onChange={(e) => {
                                            const internalNotes = [...(profile.internalNotes || [])]
                                            internalNotes[index] = {
                                                ...internalNotes[index],
                                                guestViewable: e.target.checked,
                                                // Every time we update the guestViewable, we also update the updateDate
                                                updateDate: new Date()
                                            }
                                            updateField('internalNotes', internalNotes)
                                        }}
                                        disabled={disabled}
                                    />
                                </Property>

                                <Property title={t('Note type')} labelAlignment={'top'}>
                                    <TextField
                                        id={`internal-note-${index}-type`}
                                        size={'small'}
                                        value={internalNote.name || ''}
                                        onChange={(e) => {
                                            const internalNotes = [...(profile.internalNotes || [])]
                                            internalNotes[index] = {
                                                ...internalNotes[index],
                                                name: e.target.value,
                                                // Every time we update the note type, we also update the updateDate
                                                updateDate: new Date()
                                            }
                                            updateField('internalNotes', internalNotes)
                                        }}
                                        fullWidth
                                        disabled={disabled}
                                    />
                                </Property>

                                {
                                    !disabled && (
                                        <Stack direction={'row'} justifyContent={'flex-end'}>
                                            <Button
                                                variant={'text'}
                                                size={'small'}
                                                color={'error'}
                                                onClick={() => {
                                                    const internalNotes = [...(profile.internalNotes || [])]
                                                    internalNotes.splice(index, 1)
                                                    updateField('internalNotes', internalNotes)
                                                }}
                                            >
                                                {t('Delete')}
                                            </Button>
                                        </Stack>
                                    )
                                }
                            </Stack>

                        </Property>
                    )
                })
            }


            <Button
                id={'add-note'}
                startIcon={<AddIcon/>}
                onClick={() => {
                    const internalNotes = [...(profile.internalNotes || [])]
                    // Assigning the insertDate and updateDate to the current date
                    internalNotes.push({
                        note: '',
                        guestViewable: false,
                        insertDate: new Date(),
                        updateDate: new Date(),
                        userEmailSource: currentUser?.email
                    })
                    updateField('internalNotes', internalNotes)
                }}
                sx={{
                    alignSelf: 'flex-start'
                }}
            >
                {t('Add note')}
            </Button>
        </Stack>
    )
}

export default InternalNotes;
